import moment from "moment-timezone";

// Old names the browser still lists (CLDR) → current IANA names. Only renames:
// country aliases such as Europe/Bratislava → Europe/Prague stay as they are.
const RENAMED_TIMEZONES = {
  "Africa/Asmera": "Africa/Asmara",
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
  "America/Catamarca": "America/Argentina/Catamarca",
  "America/Coral_Harbour": "America/Atikokan",
  "America/Cordoba": "America/Argentina/Cordoba",
  "America/Godthab": "America/Nuuk",
  "America/Indianapolis": "America/Indiana/Indianapolis",
  "America/Jujuy": "America/Argentina/Jujuy",
  "America/Louisville": "America/Kentucky/Louisville",
  "America/Mendoza": "America/Argentina/Mendoza",
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Rangoon": "Asia/Yangon",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Atlantic/Faeroe": "Atlantic/Faroe",
  "Europe/Kiev": "Europe/Kyiv",
  "Pacific/Enderbury": "Pacific/Kanton",
  "Pacific/Ponape": "Pacific/Pohnpei",
  "Pacific/Truk": "Pacific/Chuuk",
};

/**
 * Current IANA name of a zone: Europe/Kiev → Europe/Kyiv.
 *
 * @param {string} timezone
 * @returns {string}
 */
export const getCanonicalTimezone = (timezone) => RENAMED_TIMEZONES[timezone] || timezone;

/**
 * Zones to choose from in the profile and on the chart: the browser's list
 * (Intl.supportedValuesOf) under their current names, only those moment-timezone
 * can draw. The old names are kept for search.
 *
 * @param {string[]} [extraTimezones] - zones that must be there (current, UTC)
 * @returns {{ timezone: string, aliases: string[] }[]}
 */
export const getTimezoneOptions = (extraTimezones = []) => {
  const supported =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : moment.tz.names();
  const aliasesByTimezone = new Map();

  [...extraTimezones, ...supported].forEach((name) => {
    const timezone = name && getCanonicalTimezone(name);
    if (!timezone || !moment.tz.zone(timezone)) return;

    const aliases = aliasesByTimezone.get(timezone) || [];
    if (name !== timezone && !aliases.includes(name)) aliases.push(name);
    aliasesByTimezone.set(timezone, aliases);
  });

  return [...aliasesByTimezone].map(([timezone, aliases]) => ({ timezone, aliases }));
};

const MINUTES_PER_HOUR = 60;
// Shown in the list even when the browser does not name it among its zones
export const UTC_ZONE = "UTC";

const padTwoDigits = (value) => String(value).padStart(2, "0");

/**
 * Current offset of a zone from UTC as in PO: "UTC+2", "UTC-3:30".
 *
 * @param {string} timezone - IANA zone
 * @returns {string}
 */
export const formatUtcOffset = (timezone) => {
  const offsetMinutes = moment.tz(timezone).utcOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const hours = Math.floor(Math.abs(offsetMinutes) / MINUTES_PER_HOUR);
  const minutes = Math.abs(offsetMinutes) % MINUTES_PER_HOUR;

  return minutes ? `UTC${sign}${hours}:${padTwoDigits(minutes)}` : `UTC${sign}${hours}`;
};

/** @param {string} timezone - "America/New_York" → "America/New York" */
export const formatTimezoneName = (timezone) => timezone.replace(/_/g, " ");

/**
 * Zones to choose from in the profile and on the chart (getTimezoneOptions),
 * sorted by the current offset, then by name.
 * The current zone is always there; old names (Kiev) still find the new ones.
 *
 * @param {string} currentTimezone
 * @returns {{ timezone: string, name: string, offset: string, searchText: string }[]}
 */
export const buildTimezoneOptions = (currentTimezone) =>
  getTimezoneOptions([UTC_ZONE, currentTimezone])
    .map(({ timezone, aliases }) => ({
      timezone,
      aliases,
      name: formatTimezoneName(timezone),
      offset: formatUtcOffset(timezone),
      offsetMinutes: moment.tz(timezone).utcOffset(),
    }))
    .sort((first, second) =>
      first.offsetMinutes !== second.offsetMinutes
        ? first.offsetMinutes - second.offsetMinutes
        : first.name.localeCompare(second.name),
    )
    .map(({ timezone, aliases, name, offset }) => ({
      timezone,
      name,
      offset,
      searchText: [name, timezone, ...aliases.map(formatTimezoneName), offset]
        .join(" ")
        .toLowerCase(),
    }));

export const convertToUserTimezone = (utcTime, format = "YYYY-MM-DD HH:mm:ss") => {
  const savedTimezone = localStorage.getItem("userTimezone");
  const userTimezone = savedTimezone ? savedTimezone : moment.tz.guess();

  return moment.utc(utcTime).tz(userTimezone).format(format);
};

export const getUserTimezone = () => {
  const savedTimezone = localStorage.getItem("userTimezone");
  return getCanonicalTimezone(savedTimezone ? savedTimezone : moment.tz.guess());
};

export const formatUTC = (timestamp, format = "YYYY-MM-DD-HH:mm") =>
  moment(timestamp * 1000).tz("UTC").format(format);

export const getAdjustedTime = (timestamp, offset, unit = "minute") =>
  moment(timestamp * 1000).tz("UTC").subtract(offset, unit).format("YYYY-MM-DD-HH:mm");

/**
 * The profile time zone saved in this browser (localStorage
 * "tradingview.chartproperties"). The profile and the chart read it first:
 * without a saved zone the backend sends the one by IP, and a zone saved on
 * the server is copied here when the user loads (syncSavedTimezone).
 *
 * @returns {string} IANA zone or "" when none is saved
 */
export const getTradingViewTimezone = () => {
  try {
    const raw = localStorage.getItem("tradingview.chartproperties");
    if (!raw) return "";

    const parsed = JSON.parse(raw);
    return typeof parsed?.timezone === "string" ? getCanonicalTimezone(parsed.timezone) : "";
  } catch (error) {
    return "";
  }
};

/**
 * Saves the profile time zone where the profile page reads it first
 * (localStorage "tradingview.chartproperties", see useProfileUserData).
 * Called after the time zone is saved to the profile.
 *
 * @param {string} nextTimezone - IANA zone
 */
export const upsertTradingViewTimezone = (nextTimezone) => {
  if (!nextTimezone) return;

  try {
    const raw = localStorage.getItem(
      "tradingview.chartproperties",
    );

    const parsed = raw ? JSON.parse(raw) : {};

    const safeObject =
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
        ? parsed
        : {};

    localStorage.setItem(
      "tradingview.chartproperties",
      JSON.stringify({
        ...safeObject,
        timezone: nextTimezone,
      }),
    );
  } catch (error) {
    localStorage.setItem(
      "tradingview.chartproperties",
      JSON.stringify({
        timezone: nextTimezone,
      }),
    );
  }
};

/**
 * A zone the user saved on the server wins over the one saved in this
 * browser: it is written where the profile and the chart read it first.
 * Older backends send no timezone_saved, then the browser's zone stays.
 *
 * @param {object|null} user - user from GET /users/me
 */
export const syncSavedTimezone = (user) => {
  if (user?.timezone_saved && user.timezone) upsertTradingViewTimezone(user.timezone);
};
